import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  MarkerType,
  Node,
  NodeChange,
  Position,
  ReactFlowInstance,
  useEdgesState,
  useNodesState
} from "reactflow";
import "reactflow/dist/style.css";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import { BoardService } from "./board-service";
import "./project-transitions.scss";
import { BoardStatus, BoardTransition, Project, StatusCategory, TransitionRule } from "./types";
import { useBoardConfig } from "./use-board";

interface ProjectTransitionsProps {
  projectKey: string;
  // When false, the graph is view-only: no drag/connect/reroute/delete, no toolbar, no rule editing.
  canManage: boolean;
}

interface RuleOption {
  kind: string;
  label: string;
}

const CONDITION_OPTIONS: RuleOption[] = [
  { kind: "assignee", label: "Assignee" },
  { kind: "reporter", label: "Reporter" }
];
const VALIDATOR_OPTIONS: RuleOption[] = [
  { kind: "assignee_required", label: "Assignee set" },
  { kind: "estimate_required", label: "Estimate set" },
  { kind: "subtasks_done", label: "Sub-tasks done" },
  { kind: "not_blocked", label: "Not blocked" }
];

const ANY: string = "__any__";
// The undo modifier differs by OS (⌘ on Mac, Ctrl elsewhere); the keyboard handler accepts both.
const IS_MAC: boolean = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const UNDO_HINT: string = IS_MAC ? "⌘Z" : "Ctrl+Z";
// Left→right lanes by category so a workflow reads as a pipeline. The Any node sits left of all.
const CATEGORY_X: Record<StatusCategory, number> = { todo: 260, in_progress: 500, done: 740 };
const ROW_H: number = 92;
const TOP: number = 30;
const HISTORY_LIMIT: number = 50;
const CATEGORY_FILL: Record<StatusCategory, string> = {
  todo: "var(--dark-2)",
  in_progress: "var(--blue-1)",
  done: "var(--green-1)"
};

function transitionId(fromStatusId: string | null, toStatusId: string): string {
  return `${fromStatusId ?? ANY}->${toStatusId}`;
}

// "From any status you can reach this one", for every status — the clean, equivalent form of the
// permissive default (one edge from the Any node to each status, not an N² mesh). Allow-list
// enforcement treats no-transitions as unrestricted, so this matches it; deleting an Any→X edge
// forbids reaching X from anywhere (X becomes unreachable). Drag explicit status→status edges for
// finer control.
function allAllowed(statuses: BoardStatus[]): BoardTransition[] {
  return statuses.map((s: BoardStatus) => ({
    id: transitionId(null, s.id),
    fromStatusId: null,
    toStatusId: s.id,
    conditions: [],
    validators: []
  }));
}

function statusNodeStyle(category: StatusCategory, inbound: boolean): React.CSSProperties {
  return {
    background: CATEGORY_FILL[category],
    border: inbound ? "1px solid var(--dark-3)" : "2px solid var(--orange-5)",
    borderRadius: "var(--radius-m)",
    fontSize: 12,
    width: 150,
    color: "var(--dark-11)"
  };
}

const ANY_NODE_STYLE: React.CSSProperties = {
  background: "var(--dark-1)",
  border: "1px dashed var(--dark-5)",
  borderRadius: "var(--radius-m)",
  fontSize: 11,
  width: 110,
  color: "var(--dark-6)"
};

// Auto-layout: status nodes in category lanes (todo → in_progress → done), the Any node centered
// to the left. Returns fully-styled nodes; used on first render, when the status set changes, and
// by the Auto-arrange button. Manual drags live on top of this until the next re-arrange.
function layoutNodes(statuses: BoardStatus[], transitions: BoardTransition[], unrestricted: boolean): Node[] {
  const laneCounts: Record<string, number> = {};
  for (const s of statuses) laneCounts[s.category] = (laneCounts[s.category] ?? 0) + 1;
  const maxLane: number = Math.max(1, ...Object.values(laneCounts));
  const anyY: number = TOP + ((maxLane - 1) * ROW_H) / 2;
  const laneIndex: Record<string, number> = {};
  const nodes: Node[] = statuses.map((status: BoardStatus) => {
    const i: number = laneIndex[status.category] ?? 0;
    laneIndex[status.category] = i + 1;
    const inbound: boolean = unrestricted || transitions.some((t: BoardTransition) => t.toStatusId === status.id);
    return {
      id: status.id,
      position: { x: CATEGORY_X[status.category], y: TOP + i * ROW_H },
      data: { label: status.name },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: statusNodeStyle(status.category, inbound)
    };
  });
  nodes.push({
    id: ANY,
    position: { x: 30, y: anyY },
    data: { label: "Any status" },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: ANY_NODE_STYLE
  });
  return nodes;
}

// The project's workflow as an editable diagram. Statuses are nodes you can drag; an allowed move is
// an arrow you can draw (drag node→node), re-route (grab an arrow end, drop on another status), or
// delete (drop the end in empty space, or select + Forbid). The permissive default is "Any status →
// each status" (a clean star); prune arrows to forbid paths. Edits are optimistic and persist in the
// background.
const ProjectTransitions: React.FC<ProjectTransitionsProps> = ({ projectKey, canManage }) => {
  const { config } = useBoardConfig();
  const project: Project | undefined = config.projects.find((entry: Project) => entry.key === projectKey);
  const statuses: BoardStatus[] = useMemo(
    () => config.statuses.filter((status: BoardStatus) => status.projectKey === projectKey),
    [config.statuses, projectKey]
  );

  // The persisted (enforced) transitions; empty = unrestricted. `display` is what the diagram shows:
  // the explicit set, or the Any→each permissive default when empty.
  const configTransitions: BoardTransition[] = useMemo(() => project?.transitions ?? [], [project?.transitions]);
  const [local, setLocal] = useState<BoardTransition[]>(configTransitions);
  const pending = useRef<number>(0);
  const configKey: string = JSON.stringify(configTransitions);
  // Adopt server truth only when no write is in flight (so optimistic edits aren't clobbered).
  useEffect(() => {
    if (pending.current === 0) setLocal(configTransitions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialized config
  }, [configKey]);

  // Memoized: `allAllowed` builds a fresh array, so an inline `display` would change identity every
  // render and drive the edge-sync effect (which depends on it) into an infinite setState loop.
  const display: BoardTransition[] = useMemo(
    () => (local.length > 0 ? local : allAllowed(statuses)),
    [local, statuses]
  );
  const isUnrestricted: boolean = local.length === 0;
  // Latest local/unrestricted for the layout effect & Auto-arrange without re-laying-out on edits.
  const localRef = useRef<BoardTransition[]>(local);
  localRef.current = local;
  const unrestrictedRef = useRef<boolean>(isUnrestricted);
  unrestrictedRef.current = isUnrestricted;

  const persist = (next: BoardTransition[]): void => {
    setLocal(next);
    pending.current += 1;
    void BoardService.setProjectTransitions(projectKey, next).finally(() => {
      pending.current -= 1;
    });
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected: BoardTransition | undefined = display.find((t: BoardTransition) => t.id === selectedId);

  // ── Undo / redo over the transition set (the persisted state; node positions stay view-only). Every
  // user edit goes through `apply`, which snapshots the pre-edit `local` onto the past stack and clears
  // the redo stack. undo/redo move between the two and re-persist the chosen snapshot. ──
  const [past, setPast] = useState<BoardTransition[][]>([]);
  const [future, setFuture] = useState<BoardTransition[][]>([]);
  const apply = (next: BoardTransition[]): void => {
    setPast((stack: BoardTransition[][]) => [...stack, local].slice(-HISTORY_LIMIT));
    setFuture([]);
    persist(next);
  };
  const undo = (): void => {
    if (past.length === 0) return;
    const previous: BoardTransition[] = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture([local, ...future]);
    setSelectedId(null);
    persist(previous);
  };
  const redo = (): void => {
    if (future.length === 0) return;
    const next: BoardTransition[] = future[0];
    setFuture(future.slice(1));
    setPast([...past, local]);
    setSelectedId(null);
    persist(next);
  };
  // Refs so the window keyboard handler always calls the latest closures without re-registering.
  const undoRef = useRef<() => void>(undo);
  undoRef.current = undo;
  const redoRef = useRef<() => void>(redo);
  redoRef.current = redo;
  // Clear history when switching projects (the snapshots belong to the previous project).
  useEffect(() => {
    setPast([]);
    setFuture([]);
  }, [projectKey]);
  // Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z (or Ctrl+Y) = redo — unless typing in a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target: EventTarget | null = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return;
      }
      const meta: boolean = event.metaKey || event.ctrlKey;
      if (!meta) return;
      const key: string = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        undoRef.current();
      } else if ((key === "z" && event.shiftKey) || key === "y") {
        event.preventDefault();
        redoRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Graph state (ReactFlow owns interaction/selection; positions are manual, styling is derived) ──
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const rfRef = useRef<ReactFlowInstance | null>(null);

  const statusName = (statusId: string): string =>
    statuses.find((s: BoardStatus) => s.id === statusId)?.name ?? statusId;

  // Height fits the tallest lane — compact for a small workflow, no fixed void.
  const canvasHeight: number = useMemo(() => {
    const laneCounts: Record<string, number> = {};
    for (const s of statuses) laneCounts[s.category] = (laneCounts[s.category] ?? 0) + 1;
    const maxLane: number = Math.max(1, ...Object.values(laneCounts));
    return Math.min(560, Math.max(240, 90 + maxLane * ROW_H));
  }, [statuses]);

  // Lay out positions only when the status SET changes (add/remove/rename) — not on every edit, so a
  // manual drag survives toggling moves. Styling (the unreachable ring) is refreshed separately.
  const statusKey: string = statuses.map((s: BoardStatus) => s.id).join(",");
  useEffect(() => {
    setRfNodes(layoutNodes(statuses, localRef.current, unrestrictedRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-layout only on structural change
  }, [statusKey, setRfNodes]);

  // Refresh the unreachable ring in place (preserve manual positions) as transitions change.
  useEffect(() => {
    setRfNodes((nodes: Node[]) =>
      nodes.map((node: Node) => {
        if (node.id === ANY) return node;
        const status: BoardStatus | undefined = statuses.find((s: BoardStatus) => s.id === node.id);
        if (!status) return node;
        const inbound: boolean = isUnrestricted || local.some((t: BoardTransition) => t.toStatusId === node.id);
        return { ...node, style: statusNodeStyle(status.category, inbound) };
      })
    );
  }, [local, isUnrestricted, statuses, setRfNodes]);

  useEffect(() => {
    const edges: Edge[] = display.map((t: BoardTransition) => {
      const gated: boolean = t.conditions.length > 0 || t.validators.length > 0;
      return {
        id: t.id,
        source: t.fromStatusId ?? ANY,
        target: t.toStatusId,
        markerEnd: { type: MarkerType.ArrowClosed },
        selected: t.id === selectedId,
        label: gated ? "rules" : undefined,
        style: {
          stroke: t.id === selectedId ? "var(--blue-6)" : gated ? "var(--orange-5)" : "var(--dark-4)",
          strokeWidth: t.id === selectedId ? 2 : 1,
          opacity: isUnrestricted ? 0.45 : 1
        }
      };
    });
    setRfEdges(edges);
  }, [display, selectedId, isUnrestricted, setRfEdges]);

  const autoArrange = (): void => {
    setRfNodes(layoutNodes(statuses, localRef.current, unrestrictedRef.current));
    window.requestAnimationFrame(() => rfRef.current?.fitView({ padding: 0.2, duration: 250 }));
  };

  const onConnect = (params: Connection): void => {
    if (!canManage) return;
    if (!params.source || !params.target || params.source === params.target) return;
    const from: string | null = params.source === ANY ? null : params.source;
    const id: string = transitionId(from, params.target);
    if (display.some((t: BoardTransition) => t.id === id)) return;
    apply([...display, { id, fromStatusId: from, toStatusId: params.target, conditions: [], validators: [] }]);
  };

  // Re-routing: when an arrow end is dropped on another status, rewrite that move (carry its rules).
  // A `false` flag means it was dropped in empty space → the move is deleted (onEdgeUpdateEnd).
  const edgeUpdateOk = useRef<boolean>(true);
  const onEdgeUpdateStart = (): void => {
    edgeUpdateOk.current = false;
  };
  const onEdgeUpdate = (oldEdge: Edge, conn: Connection): void => {
    edgeUpdateOk.current = true;
    if (!canManage) return;
    if (!conn.source || !conn.target || conn.source === conn.target) return;
    const from: string | null = conn.source === ANY ? null : conn.source;
    const id: string = transitionId(from, conn.target);
    const old: BoardTransition | undefined = display.find((t: BoardTransition) => t.id === oldEdge.id);
    const next: BoardTransition[] = display.filter((t: BoardTransition) => t.id !== oldEdge.id && t.id !== id);
    next.push({
      id,
      fromStatusId: from,
      toStatusId: conn.target,
      conditions: old?.conditions ?? [],
      validators: old?.validators ?? []
    });
    apply(next);
    if (selectedId === oldEdge.id) setSelectedId(id);
  };
  const onEdgeUpdateEnd = (_event: MouseEvent | TouchEvent, edge: Edge): void => {
    if (!edgeUpdateOk.current) {
      apply(display.filter((t: BoardTransition) => t.id !== edge.id));
      if (selectedId === edge.id) setSelectedId(null);
    }
    edgeUpdateOk.current = true;
  };

  const onEdgesDelete = (deleted: Edge[]): void => {
    if (!canManage) return;
    const ids: Set<string> = new Set<string>(deleted.map((e: Edge) => e.id));
    apply(display.filter((t: BoardTransition) => !ids.has(t.id)));
    setSelectedId(null);
  };

  const updateSelectedRules = (key: "conditions" | "validators", kinds: string[]): void => {
    if (!selected) return;
    const rules: TransitionRule[] = kinds.map((kind: string) => ({ kind }));
    apply(display.map((t: BoardTransition) => (t.id === selected.id ? { ...t, [key]: rules } : t)));
  };

  return (
    <section className="kuika-transitions">
      <div className="kuika-transitions__head">
        <div>
          <div className="kuika-transitions__title">Transitions</div>
          <div className="kuika-transitions__sub">
            Each arrow is an allowed move.{" "}
            <strong>
              {isUnrestricted ? "All moves are allowed (Any status → each)" : "Only the arrows below are allowed"}
            </strong>{" "}
            — drag from one status to another to add a move, grab an arrow's end to re-route it (drop in empty space to
            remove), and select an arrow to gate or delete it. A status with an orange ring has no incoming move (it's
            unreachable). Undo any change with {UNDO_HINT}.
          </div>
        </div>
        {canManage && (
          <div className="kuika-transitions__head-actions">
            <KuikaButton type="tertiary" color="neutral" size="sm" onClick={undo} disabled={past.length === 0}>
              Undo
            </KuikaButton>
            <KuikaButton type="tertiary" color="neutral" size="sm" onClick={redo} disabled={future.length === 0}>
              Redo
            </KuikaButton>
            <KuikaButton type="tertiary" color="neutral" size="sm" onClick={autoArrange}>
              Auto-arrange
            </KuikaButton>
            {!isUnrestricted && (
              <KuikaButton type="tertiary" color="neutral" size="sm" onClick={() => apply([])}>
                Reset to all allowed
              </KuikaButton>
            )}
          </div>
        )}
      </div>

      <div className="kuika-transitions__canvas" style={{ height: canvasHeight }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onInit={(instance: ReactFlowInstance) => {
            rfRef.current = instance;
          }}
          onNodesChange={(changes: NodeChange[]) => onNodesChange(changes)}
          onEdgesChange={(changes: EdgeChange[]) => onEdgesChange(changes)}
          onConnect={onConnect}
          onEdgeUpdate={onEdgeUpdate}
          onEdgeUpdateStart={onEdgeUpdateStart}
          onEdgeUpdateEnd={onEdgeUpdateEnd}
          onEdgesDelete={onEdgesDelete}
          onEdgeClick={(_event, edge: Edge) => setSelectedId(edge.id)}
          onPaneClick={() => setSelectedId(null)}
          nodesDraggable={canManage}
          nodesConnectable={canManage}
          edgesUpdatable={canManage}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.4}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} color="var(--dark-2)" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      {selected && canManage && (
        <div className="kuika-transitions__panel">
          <div className="kuika-transitions__panel-title">
            {selected.fromStatusId == null ? "Any status" : statusName(selected.fromStatusId)} →{" "}
            {statusName(selected.toStatusId)}
          </div>
          <div className="kuika-transitions__panel-rules">
            <label className="kuika-transitions__rule">
              <span className="kuika-transitions__rule-label">Who can</span>
              <KuikaSelectbox
                size="sm"
                mode="multiple"
                dropdownMatchSelectWidth={false}
                placeholder="Anyone"
                value={selected.conditions.map((r: TransitionRule) => r.kind)}
                onChange={(value: string | number | (string | number)[] | null) =>
                  updateSelectedRules("conditions", Array.isArray(value) ? value.map(String) : [])
                }
                options={CONDITION_OPTIONS.map((o: RuleOption) => ({ value: o.kind, label: o.label }))}
              />
            </label>
            <label className="kuika-transitions__rule">
              <span className="kuika-transitions__rule-label">Requires</span>
              <KuikaSelectbox
                size="sm"
                mode="multiple"
                dropdownMatchSelectWidth={false}
                placeholder="Nothing"
                value={selected.validators.map((r: TransitionRule) => r.kind)}
                onChange={(value: string | number | (string | number)[] | null) =>
                  updateSelectedRules("validators", Array.isArray(value) ? value.map(String) : [])
                }
                options={VALIDATOR_OPTIONS.map((o: RuleOption) => ({ value: o.kind, label: o.label }))}
              />
            </label>
          </div>
          <button
            type="button"
            className="kuika-transitions__panel-delete"
            onClick={() => onEdgesDelete([{ id: selected.id } as Edge])}
          >
            Forbid this move
          </button>
        </div>
      )}
    </section>
  );
};

export default ProjectTransitions;
